import { api } from './api';
import { SupportTicket, SupportTicketListResponse, ChatMessage, Id, TicketEvent, TicketNote, CannedReply } from '../types/Support';

// GET all support tickets (paginated & filtered)
export const listSupportTickets = (
    page: number = 1,
    limit: number = 20,
    filters?: { status?: string; issueType?: string; rideId?: number; priority?: string; cityId?: string; assigneeId?: number; unassigned?: boolean; from?: string; to?: string }
) => {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    if (filters?.status) params.append('status', filters.status);
    if (filters?.issueType) params.append('issueType', filters.issueType);
    if (filters?.rideId) params.append('rideId', filters.rideId.toString());
    if (filters?.priority) params.append('priority', filters.priority);
    if (filters?.cityId) params.append('cityId', filters.cityId);
    if (typeof filters?.assigneeId === 'number') params.append('assigneeId', filters.assigneeId.toString());
    if (filters?.unassigned) params.append('unassigned', 'true');
    if (filters?.from) params.append('from', filters.from);
    if (filters?.to) params.append('to', filters.to);

    return api.get<SupportTicketListResponse>(`/admin/support-tickets?${params.toString()}`);
};

// GET single ticket details
export const getSupportTicket = (id: Id) => {
    return api.get<SupportTicket>(`/admin/support-ticket/${id}`);
};

// UPDATE ticket (status/priority/assignee/tags/…). Backend value-validates → 400 on bad enum.
export const updateSupportTicket = (id: Id, data: Partial<SupportTicket>) => {
    return api.patch<SupportTicket>(`/admin/support-ticket/${id}`, data);
};

// GET chat history for a ticket
export const getTicketMessages = (id: Id) => {
    return api.get<ChatMessage[]>(`/admin/support-ticket/${id}/messages`);
};

// POST a new message (reply)
export const sendTicketMessage = (id: Id, content: string) => {
    return api.post<ChatMessage>(`/admin/support-ticket/${id}/message`, { content });
};

// Claim a ticket (optimistic lock — first agent wins; 409 if already claimed).
export const claimSupportTicket = (id: Id) => {
    return api.post<{ message: string; claimed: boolean }>(`/admin/support-ticket/${id}/claim`, {});
};

// (Re)assign a ticket to an agent (or null to unassign).
export const assignSupportTicket = (id: Id, assigneeId: number | null) => {
    return api.post<{ message: string; assigneeId: number | null }>(`/admin/support-ticket/${id}/assign`, { assigneeId });
};

// Assignable-staff directory (ACTIVE staff, slim payload) for the assign picker + assignee names.
export const listSupportAgents = () => {
    return api.get<{ agents: { id: number; name: string; role: string }[] }>(`/admin/support-agents`);
};

// Ticket audit trail — created / assigned / sla_breach / csat / reopened / … for the Timeline tab.
export const getTicketEvents = (id: Id) => {
    return api.get<{ events: TicketEvent[] }>(`/admin/support-ticket/${id}/events`);
};

// Internal notes — agent-only; the customer never sees these.
export const getTicketNotes = (id: Id) => {
    return api.get<{ notes: TicketNote[] }>(`/admin/support-ticket/${id}/notes`);
};

// POST a new internal note (body 1..2000 chars → 201 {note} | 400).
export const addTicketNote = (id: Id, body: string) => {
    return api.post<{ note: TicketNote }>(`/admin/support-ticket/${id}/note`, { body });
};

// Canned replies for the ⚡ quick-insert menu next to the reply box.
export const listCannedReplies = () => {
    return api.get<{ replies: CannedReply[] }>(`/admin/support-canned`);
};

// Search tickets across description / publicNumber / phone / uid, with optional city/status scoping.
export const searchSupportTickets = (q: string, opts?: { cityId?: string; status?: string; limit?: number; from?: string; to?: string }) => {
    const params = new URLSearchParams();
    params.append('q', q);
    if (opts?.cityId) params.append('cityId', opts.cityId);
    if (opts?.status) params.append('status', opts.status);
    if (opts?.limit) params.append('limit', opts.limit.toString());
    if (opts?.from) params.append('from', opts.from);
    if (opts?.to) params.append('to', opts.to);
    return api.get<{ tickets: SupportTicket[]; total: number }>(`/admin/support-tickets/search?${params.toString()}`);
};